import CodeIcon from "@mui/icons-material/Code";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import { Panel } from "@poseidon/ui/components/Panel";
import { SelectDialog } from "@poseidon/ui/components/SelectDialog";
import { ShowMoreDialog } from "@poseidon/ui/components/ShowMoreDialog";
import { db } from "@poseidon/firebase";
import { getDate } from "@poseidon/firebase/timestamp";
import { niceDate } from "@poseidon/utils/date";
import { JsonViewer } from "@textea/json-viewer";
import { collection, orderBy, query } from "firebase/firestore";
import { Formik } from "formik";
import { useCallback, useEffect, useState } from "react";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { IdvCheckStatus } from "../../../IdvCheck/IdvCheckStatus";
import { StartIdvCheckButton } from "../../../IdvCheck/StartIdvCheckButton";
import { CountriesField } from "../../../Sailors/components/edit/CountriesField";
import { DocumentTypeField } from "../../../Sailors/components/edit/DocumentTypeField";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";

const mapIdvCheckToSelectDialogOption = ({ id, requestedAt }) =>
  requestedAt
    ? {
        label: niceDate(requestedAt),
        secondary: requestedAt.toLocaleString(),
        value: id,
      }
    : "";

const idvChecksConverter = {
  fromFirestore(snapshot) {
    const data = snapshot.data();
    return {
      ...data,
      id: snapshot.id,
      requestedAt: getDate(data.requestedAt),
    };
  },
  toFirestore: (data) => data,
};

export const IdvChecks = ({ sailorId, riskProfileIdvCheckState }) => {
  const [showNewCheckForm, setShowNewCheckForm] = useState(false);
  const [currentIdvCheck, setCurrentIdvCheck] = useState(null);
  const [idvChecks, loading, error] = useCollectionData(
    query(
      collection(db, "sailorRiskProfiles", sailorId, "idvChecks").withConverter(
        idvChecksConverter
      ),
      orderBy("requestedAt", "desc")
    )
  );

  const selectIdvCheckOnChange = useCallback(
    (value) =>
      setCurrentIdvCheck(idvChecks?.find((check) => check.id === value)),
    [idvChecks]
  );

  const { setError } = useErrorMessage();

  useEffect(() => {
    if (!loading && idvChecks?.length > 0) {
      setCurrentIdvCheck(idvChecks[0]);
    }

    if (error) {
      setError(error);
    }
  }, [error, idvChecks, loading, setError]);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Formik
      initialValues={{
        issuingCountry: "",
        documentType: "",
      }}
    >
      {({ values }) => {
        const invalidNewCheckForm =
          !values.documentType || !values.issuingCountry;
        return (
          <Panel
            title="Checks"
            expandable
            actions={
              <>
                {currentIdvCheck && (
                  <>
                    <Button variant="outlined" size="small" disabled>
                      View PDF
                    </Button>
                    <Button
                      sx={{ ml: 1 }}
                      variant="outlined"
                      size="small"
                      component={currentIdvCheck.result?.id ? "a" : undefined}
                      disabled={!currentIdvCheck.result?.id}
                      href={`${process.env.NX_SMARTSEARCH_PORTAL_URL}doccheck/results/${currentIdvCheck.result?.id}`}
                      target="_blank"
                      rel="nofollow noopener"
                    >
                      Goto Portal
                    </Button>
                    <ShowMoreDialog
                      buttonText="View raw data"
                      buttonVariant="outlined"
                      title="Raw Search Data"
                      buttonProps={{
                        size: "small",
                        startIcon: <CodeIcon />,
                      }}
                    >
                      <JsonViewer
                        value={currentIdvCheck ?? {}}
                        displayDataTypes={false}
                      />
                    </ShowMoreDialog>
                  </>
                )}
                {showNewCheckForm ? (
                  <StartIdvCheckButton
                    isFirstCheck={!idvChecks?.length}
                    sailorIdvDetails={{
                      sailorId,
                      issuingCountry: values.issuingCountry.id,
                      documentType: values.documentType.id,
                    }}
                    disabled={
                      riskProfileIdvCheckState === "blocked" ||
                      invalidNewCheckForm
                    }
                  />
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonSearchIcon />}
                    onClick={() => setShowNewCheckForm(true)}
                  >
                    New ID&V Invite
                  </Button>
                )}
              </>
            }
          >
            <Stack spacing={2} sx={{ mb: 2 }}>
              <Typography variant="body2">
                {currentIdvCheck && (
                  <>
                    Check from{" "}
                    {currentIdvCheck?.requestedAt &&
                      niceDate(getDate(currentIdvCheck?.requestedAt))}{" "}
                    ({currentIdvCheck?.requestedAt?.toLocaleString()})
                  </>
                )}
                {idvChecks?.length > 1 && (
                  <SelectDialog
                    buttonText="View other"
                    buttonVariant="text"
                    title="Select IDV Check"
                    buttonProps={{ size: "small" }}
                    options={idvChecks}
                    mapOption={mapIdvCheckToSelectDialogOption}
                    onChange={selectIdvCheckOnChange}
                    value={currentIdvCheck}
                  />
                )}
                {idvChecks?.length > 1 &&
                  currentIdvCheck?.id !== idvChecks[0].id && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setCurrentIdvCheck(idvChecks[0])}
                    >
                      View latest
                    </Button>
                  )}
              </Typography>
              {!loading && idvChecks?.length === 0 && (
                <Typography variant="caption" sx={{ mb: 2, opacity: 0.7 }}>
                  No IDV Checks created yet
                </Typography>
              )}
              {currentIdvCheck && (
                <IdvCheckStatus loading={loading} idvCheck={currentIdvCheck} />
              )}
              {showNewCheckForm && (
                <Form>
                  <Typography variant="subtitle" sx={{ my: 2, opacity: 0.7 }}>
                    New IDV Check
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <CountriesField
                      label="Document issuing country"
                      name="issuingCountry"
                      withCode
                      disabled={riskProfileIdvCheckState === "blocked"}
                    />
                    <DocumentTypeField
                      label="Document type"
                      name="documentType"
                      disabled={riskProfileIdvCheckState === "blocked"}
                    />
                  </Stack>
                </Form>
              )}
            </Stack>
          </Panel>
        );
      }}
    </Formik>
  );
};
